import { Injectable, Injector, inject, signal } from '@angular/core';
import { AuthService } from '../../auth.service';
import { ZardDialogService } from '../components/ui-components';
import { openSettingsDialog, settingsDialogOpening } from '../components/dialogs/settings-dialog/open-settings-dialog';
import { AddonStateService } from './addon-state.service';
import { LawFirmUsersCacheService } from './law-firm-users-cache.service';
import { LimitsStateService } from './limits-state.service';
import { SeatOptionsService } from './seat-options.service';
import { SubscriptionStatusService } from './subscription-status.service';
import { UserSeatService } from './user-seat.service';
import { SettingsDialogStateService } from './settings-dialog-state.service';

/** Header the backend honours on firm-administration endpoints for admin/support callers. */
export const ACT_AS_HEADER = 'X-Act-As-Realm';

/** How long a restore waits for the firm's in-flight requests; later responses are dropped anyway. */
const FIRM_REQUESTS_SETTLE_TIMEOUT_MS = 15_000;

export interface ActingFor {
  slug: string;
  name: string;
}

/**
 * Silvernova staff managing another firm as its admin. Active only while the settings dialog
 * opened for that firm is open; the shared firm caches are reloaded on the way in and out.
 */
@Injectable({ providedIn: 'root' })
export class ActAsService {
  private readonly auth = inject(AuthService);
  private readonly settingsDialogState = inject(SettingsDialogStateService);
  // The firm caches are resolved on use: the interceptor creates this service, and caches that
  // request on construction would re-enter it while it is being created (NG0200).
  private readonly injector = inject(Injector);

  readonly actingFor = signal<ActingFor | null>(null);
  /** The firm's data is loaded; until then the dialog shows a loader instead of stale data. */
  readonly ready = signal(false);
  /** Loading the firm's data failed; acting has been stopped and the dialog says so. */
  readonly failed = signal(false);
  private refreshing: Promise<void> | null = null;
  /** Restoring the caller's own firm after a session; a new session waits for it. */
  private ending: Promise<void> | null = null;
  /** A session is opening, running or being restored: no new one can start yet. */
  readonly busy = signal(false);
  /** Bumped whenever acting starts or ends: responses to an older session's requests are dropped. */
  private epoch = 0;
  private inFlight = 0;
  private idleWaiters: (() => void)[] = [];

  async open(dialog: ZardDialogService, firm: ActingFor): Promise<void> {
    // One firm at a time, and never on top of an open or opening settings dialog.
    if (!this.auth.canUseSysadmin || this.actingFor() !== null
        || this.settingsDialogState.isOpen() || settingsDialogOpening()) {
      return;
    }
    // A previous session's restore must finish first, or its late own-firm responses could
    // overwrite the new firm's data under the new banner.
    await this.ending;
    if (this.actingFor() !== null || this.settingsDialogState.isOpen() || settingsDialogOpening()) {
      return;
    }
    this.actingFor.set(firm);
    this.epoch++;
    this.busy.set(true);
    this.ready.set(false);
    this.failed.set(false);
    const refreshing = this.refreshFirmState();
    this.refreshing = refreshing;

    const opened = await openSettingsDialog(dialog, { initialTab: 'userAdministration', actingFor: firm })
      .catch(() => false);
    if (!opened) {
      // No acting dialog exists to end this, so roll back here.
      await this.end();
      return;
    }
    try {
      await refreshing;
      if (this.actingFor() === firm) {
        this.ready.set(true);
      }
    } catch {
      // Half-switched caches must never be shown as the firm's: stop acting instead.
      if (this.actingFor() === firm) {
        this.failed.set(true);
        await this.end();
      }
    }
  }

  /** Called when the dialog closes (or opening fails): back to the caller's own firm. */
  end(): Promise<void> {
    if (this.actingFor() === null) {
      return this.ending ?? Promise.resolve();
    }
    // Stop sending the header right away; later user or background calls run as the caller.
    this.actingFor.set(null);
    this.epoch++;
    this.ready.set(false);
    const pending = this.refreshing;
    this.refreshing = null;
    const ending = (async () => {
      // Responses requested for the firm may still land; reload only after them so ours wins.
      await pending?.catch(() => undefined);
      await this.firmRequestsSettled();
      try {
        await this.refreshFirmState();
      } catch {
        // The caches may still hold the firm's data: start over clean rather than show it as ours.
        this.reloadPage();
      }
    })().finally(() => {
      if (this.ending === ending) {
        this.ending = null;
        this.busy.set(false);
      }
    });
    this.ending = ending;
    return ending;
  }

  /** For the interceptor: registers a request sent for the firm. */
  trackFirmRequest(): { epoch: number; done: () => void } {
    this.inFlight++;
    let finished = false;
    const done = () => {
      if (finished) {
        return;
      }
      finished = true;
      this.inFlight--;
      if (this.inFlight === 0) {
        this.idleWaiters.splice(0).forEach(resolve => resolve());
      }
    };
    return { epoch: this.epoch, done };
  }

  /** False once the session a request was sent in has ended or been replaced. */
  isCurrentEpoch(epoch: number): boolean {
    return epoch === this.epoch;
  }

  private reloadPage(): void {
    window.location.reload();
  }

  private firmRequestsSettled(): Promise<void> {
    if (this.inFlight === 0) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      this.idleWaiters.push(resolve);
      setTimeout(resolve, FIRM_REQUESTS_SETTLE_TIMEOUT_MS);
    });
  }

  private async refreshFirmState(): Promise<void> {
    const seatOptions = this.injector.get(SeatOptionsService);
    seatOptions.reset();
    this.injector.get(LawFirmUsersCacheService).refresh();
    // allSettled: one failure must not cut the wait for the others short.
    const results = await Promise.allSettled([
      this.injector.get(UserSeatService).refresh(),
      this.injector.get(LimitsStateService).refreshLimits(),
      this.injector.get(AddonStateService).refresh(true),
      seatOptions.loadSeatOptions(),
      this.injector.get(SubscriptionStatusService).reload(),
    ]);
    const failure = results.find(result => result.status === 'rejected');
    if (failure) {
      throw failure.reason;
    }
  }
}
